import React, { useState } from "react";
import { api } from "@/lib/api";
import ResumeUpload from "@/components/ResumeUpload";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";

const scoreColor = (n) => n >= 75 ? "text-emerald-600" : n >= 50 ? "text-gold" : "text-red-600";

export default function ResumeScreen() {
  const [jd, setJd] = useState("");
  const [resume, setResume] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    if (!jd || !resume) { toast.error("Both job description and resume are required"); return; }
    setLoading(true); setResult(null);
    try {
      const r = await api.post("/ai/resume-screen", { job_description: jd, resume_text: resume });
      setResult(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Screening failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-8" data-testid="ai-screen-page">
      <div>
        <div className="label-eyebrow text-navy/50">AI · Claude Sonnet 4.5</div>
        <h1 className="serif-display text-4xl mt-2">Resume ↔ Job match</h1>
        <p className="mt-3 text-navy/60 max-w-2xl">Paste a job description and upload a candidate resume (PDF or DOCX). Our screener returns a match score, strengths, gaps and a recommendation.</p>
      </div>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-navy/10 p-6">
          <Label>Job description</Label>
          <Textarea rows={12} value={jd} onChange={(e)=>setJd(e.target.value)} placeholder="Paste JD here..." data-testid="ai-jd" className="mt-2"/>
        </div>
        <div className="bg-white border border-navy/10 p-6">
          <Label>Candidate resume</Label>
          <div className="mt-2"><ResumeUpload value={resume} onChange={setResume} testid="ai-resume"/></div>
        </div>
      </div>
      <div className="flex gap-3">
        <button className="btn-primary" onClick={run} disabled={loading} data-testid="ai-run-btn">
          <Sparkles size={14}/> {loading ? "Screening..." : "Run screening"}
        </button>
        {result && <button className="btn-outline" onClick={()=>setResult(null)} data-testid="ai-reset">Reset</button>}
      </div>

      {result && (
        <div className="bg-navy text-offwhite p-8 md:p-10 grain relative overflow-hidden" data-testid="ai-result">
          <div className="relative z-10 grid md:grid-cols-4 gap-8 items-start">
            <div>
              <div className="label-eyebrow text-gold">Match Score</div>
              <div className={`serif-display text-7xl mt-3 ${scoreColor(result.match_score)}`}>{result.match_score}</div>
              <div className="label-eyebrow text-offwhite/50 mt-1">/ 100</div>
            </div>
            <div className="md:col-span-3">
              <div className="label-eyebrow text-gold">Recommendation</div>
              <p className="serif-display italic text-2xl mt-3 leading-snug">{result.recommendation}</p>
              <p className="mt-4 text-offwhite/70 leading-relaxed">{result.summary}</p>
            </div>
          </div>
          <div className="relative z-10 grid md:grid-cols-2 gap-8 mt-10 pt-8 border-t border-white/10">
            <div>
              <div className="label-eyebrow text-gold">Strengths</div>
              <ul className="mt-3 space-y-2">
                {result.strengths.map((s, i) => <li key={i} className="flex gap-3"><span className="text-gold">+</span> {s}</li>)}
              </ul>
            </div>
            <div>
              <div className="label-eyebrow text-gold">Gaps</div>
              <ul className="mt-3 space-y-2">
                {result.gaps.map((g, i) => <li key={i} className="flex gap-3"><span className="text-red-400">–</span> {g}</li>)}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
